import API from 'api/api';
import { getListingById, getQuotesForListing } from 'domains/listing/services/listingService';

const asObject = (value) => (value && typeof value === 'object' ? value : {});

const getString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);

const getNullableString = (value) => (typeof value === 'string' && value.trim() ? value : null);

const getNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getId = (value) => {
  const record = asObject(value);
  return (
    getString(record.id) ||
    getString(record._id) ||
    getString(record.messageId) ||
    getString(record.notificationId) ||
    ''
  );
};

function normalizeThreadListing(item) {
  const record = asObject(item);
  const route = asObject(record.route);

  return {
    id: getNullableString(record.id) || getNullableString(record._id) || '',
    _id: getNullableString(record.id) || getNullableString(record._id) || '',
    humanId: getNullableString(record.humanId),
    title: getString(record.title),
    status: getString(record.status, 'open'),
    route: {
      from: getString(route.from || record.fromCity),
      to: getString(route.to || record.toCity),
    },
    createdAt: getNullableString(record.createdAt),
  };
}

function normalizeMessage(item) {
  const record = asObject(item);

  return {
    id: getId(record),
    listingId: getString(record.listingId),
    senderId: getString(record.senderId),
    senderRole:
      record.senderRole === 'partner' || record.senderRole === 'admin'
        ? record.senderRole
        : 'client',
    senderName: getNullableString(record.senderName),
    recipientId: getNullableString(record.recipientId),
    text: getString(record.text),
    isPublic: Boolean(record.isPublic ?? true),
    read: Boolean(record.read),
    createdAt: getNullableString(record.createdAt),
    translatedText: getNullableString(record.translatedText),
    originalLanguage: getNullableString(record.originalLanguage),
  };
}

export async function getMessageThreads() {
  const response = await API.get('/messages/inbox');
  const payload = asObject(response.data);
  const threads = Array.isArray(payload.threads)
    ? payload.threads
    : Array.isArray(response.data)
      ? response.data
      : [];

  return threads.map((item) => {
    const record = asObject(item);
    return {
      threadKey: getString(record.threadKey) || getString(record.listingId),
      listing: normalizeThreadListing(record.listing),
      lastMessage: record.lastMessage ? normalizeMessage(record.lastMessage) : null,
      unreadCount: getNumber(record.unreadCount, 0),
      updatedAt: getNullableString(record.updatedAt),
    };
  });
}

export async function getListingMessages(listingId) {
  if (!listingId) {
    return [];
  }

  const response = await API.get(`/listings/${listingId}/messages`);
  const payload = response.data;

  if (Array.isArray(payload)) {
    return payload.map(normalizeMessage);
  }

  const messages = asObject(payload).messages;
  return Array.isArray(messages) ? messages.map(normalizeMessage) : [];
}

export async function sendListingMessage(listingId, text) {
  if (!listingId || !String(text || '').trim()) {
    throw new Error('Message text is required.');
  }

  const response = await API.post(`/listings/${listingId}/messages`, {
    text: String(text).trim(),
    isPublic: true,
  });

  return normalizeMessage(response.data);
}

export async function getUnreadMessageCount(listingId, currentUserId) {
  if (!listingId || !currentUserId) {
    return 0;
  }

  try {
    const messages = await getListingMessages(listingId);
    return messages.filter(
      (msg) => msg.senderId !== currentUserId && !msg.read,
    ).length;
  } catch {
    return 0;
  }
}

export async function markMessageRead(messageId) {
  if (!messageId) {
    return;
  }

  await API.patch(`/messages/${messageId}/read`);
}

export async function getReviewContext(listingId) {
  const [listingResponse, quotes] = await Promise.all([
    getListingById(listingId),
    getQuotesForListing(listingId),
  ]);
  const listing = listingResponse?.listing ?? listingResponse;
  const acceptedQuote =
    (Array.isArray(quotes) ? quotes : []).find(
      (quote) => String(quote.status || quote._status || '').toLowerCase() === 'accepted',
    ) || null;

  return {
    listing,
    quotes: Array.isArray(quotes) ? quotes : [],
    acceptedQuote,
  };
}

export async function createListingReview(listingId, review) {
  if (!listingId) {
    throw new Error('Listing id is required.');
  }

  if (!review?.quoteId || !review?.partnerId) {
    throw new Error('Accepted quote information is missing.');
  }

  const response = await API.post(`/reviews/listing/${listingId}`, {
    quoteId: review.quoteId,
    partnerId: review.partnerId,
    stars: Number(review.stars),
    sentiment: review.sentiment,
    comment: String(review.comment || '').trim(),
  });

  return response.data;
}
